import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Link,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  AutoAwesome as AIIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  MarkEmailUnread as MarkEmailUnreadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { loginUser, login2FA, resendConfirmationEmail, LoginResponse, saveAuthData } from '../services/auth';
import { colors } from '../theme/expertiseTheme';
import LogoImage from '../images/Expertise_Marca_FundosClaros_V1.png';

type LoginState = 'form' | '2fa' | 'email_not_confirmed';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<LoginState>('form');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [userId2FA, setUserId2FA] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      const res: LoginResponse = await loginUser({ email, password });
      
      if (res.require2fa) {
        setUserId2FA(res.userId ?? null);
        setState('2fa');
      } else if (res.token) {
        // Salvar dados de autenticação (token + user info)
        saveAuthData(res);
        navigate('/home', { replace: true });
      } else {
        setError('Resposta inesperada da API');
      }
    } catch (err: any) {
      // Verificar se é erro de email não confirmado
      if (err.code === 'EMAIL_NOT_CONFIRMED' || 
          err.message?.toLowerCase().includes('não confirmado') ||
          err.message?.toLowerCase().includes('not confirmed')) {
        setState('email_not_confirmed');
      } else {
        setError(err.message || 'Erro ao fazer login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FA = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!userId2FA) return;
    setError('');
    setLoading(true);
    
    try {
      const res = await login2FA({ userId: userId2FA, twoFactorCode });
      if (res.token) {
        // Salvar dados de autenticação (token + user info)
        saveAuthData(res);
        navigate('/home', { replace: true });
      } else {
        setError('Resposta inesperada da API no 2FA');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao validar 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!email) {
      setError('Informe o email para reenviar a confirmação');
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      await resendConfirmationEmail({ email });
      setSuccess('Email de confirmação reenviado! Verifique sua caixa de entrada.');
    } catch (err: any) {
      // Se o endpoint não existir, mostrar mensagem alternativa
      if (err.message?.includes('404') || err.message?.includes('not found')) {
        setError('Recurso de reenvio não disponível. Verifique sua caixa de entrada ou pasta de spam.');
      } else {
        setError(err.message || 'Erro ao reenviar email');
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <AIIcon sx={{ fontSize: 20, color: colors.accent }} />,
      title: 'Ferramentas IA Avançadas',
      description: 'Acesse todas as ferramentas de IA em um único lugar',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 20, color: colors.accent }} />,
      title: 'Análise em Tempo Real',
      description: 'Monitore e analise resultados instantaneamente',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 20, color: colors.accent }} />,
      title: 'Segurança Garantida',
      description: 'Seus dados protegidos com criptografia avançada',
    },
  ];

  const renderEmailNotConfirmed = () => (
    <Box sx={{ textAlign: 'center' }}>
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 3,
        }}
      >
        <MarkEmailUnreadIcon sx={{ fontSize: 40, color: '#F59E0B' }} />
      </Box>

      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: colors.dark,
          mb: 2,
        }}
      >
        Email não confirmado
      </Typography>

      <Typography
        sx={{
          color: colors.text.secondary,
          fontSize: '0.9375rem',
          mb: 3,
          lineHeight: 1.6,
        }}
      >
        Sua conta foi criada, mas o email ainda não foi confirmado. 
        Verifique sua caixa de entrada e clique no link de confirmação.
      </Typography>

      <Box
        sx={{
          backgroundColor: colors.white,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          py: 2,
          px: 3,
          mb: 4,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            color: colors.dark,
            fontSize: '1rem',
          }}
        >
          {email}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
          {success}
        </Alert>
      )}

      <Alert 
        severity="info" 
        sx={{ 
          mb: 4, 
          textAlign: 'left',
          borderRadius: '12px',
        }}
      >
        <Typography variant="body2">
          <strong>Não recebeu o email?</strong>
          <br />
          • Verifique a pasta de spam/lixo eletrônico
          <br />
          • Aguarde alguns minutos e tente novamente
        </Typography>
      </Alert>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          onClick={handleResendConfirmation}
          disabled={loading}
          sx={{
            py: 1.25,
            px: 3,
            borderColor: colors.border,
            color: colors.text.primary,
            fontWeight: 600,
            '&:hover': {
              borderColor: colors.primary,
              backgroundColor: 'rgba(0, 99, 0, 0.05)',
            },
          }}
        >
          {loading ? 'Reenviando...' : 'Reenviar email'}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            setState('form');
            setError('');
            setSuccess('');
          }}
          sx={{
            py: 1.25,
            px: 4,
            backgroundColor: colors.accent,
            color: colors.dark,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#c9e835',
            },
          }}
        >
          Voltar ao Login
        </Button>
      </Box>
    </Box>
  );

  const render2FAForm = () => (
    <Box component="form" onSubmit={handle2FA} noValidate>
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 3,
        }}
      >
        <SecurityIcon sx={{ fontSize: 32, color: '#3B82F6' }} />
      </Box>

      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: colors.dark,
          mb: 2,
          textAlign: 'center',
        }}
      >
        Verificação em duas etapas
      </Typography>

      <Typography
        sx={{
          color: colors.text.secondary,
          fontSize: '0.9375rem',
          mb: 4,
          textAlign: 'center',
        }}
      >
        Digite o código de 6 dígitos do seu aplicativo autenticador
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        placeholder="000000"
        value={twoFactorCode}
        onChange={(e) => {
          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
          setTwoFactorCode(value);
        }}
        required
        inputProps={{
          maxLength: 6,
          style: { 
            textAlign: 'center', 
            letterSpacing: '0.5em',
            fontSize: '1.5rem',
            fontWeight: 600,
          },
        }}
        sx={{
          mb: 4,
          '& .MuiOutlinedInput-root': {
            backgroundColor: colors.white,
          },
        }}
      />

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading || twoFactorCode.length !== 6}
        sx={{
          py: 1.5,
          backgroundColor: colors.accent,
          color: colors.dark,
          fontWeight: 600,
          fontSize: '1rem',
          '&:hover': {
            backgroundColor: '#c9e835',
          },
          '&:disabled': {
            backgroundColor: colors.border,
            color: colors.text.light,
          },
        }}
      >
        {loading ? 'Verificando...' : 'Verificar'}
      </Button>

      <Button
        fullWidth
        onClick={() => {
          setState('form');
          setTwoFactorCode('');
          setUserId2FA(null);
          setError('');
        }}
        sx={{
          mt: 2,
          color: colors.text.secondary,
          '&:hover': {
            backgroundColor: 'transparent',
            color: colors.primary,
          },
        }}
      >
        Voltar ao login
      </Button>
    </Box>
  );

  const renderLoginForm = () => (
    <>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: colors.dark,
          mb: 1,
        }}
      >
        Bem-vindo de volta
      </Typography>
      <Typography
        sx={{
          color: colors.text.secondary,
          fontSize: '0.9375rem',
          mb: 4,
        }}
      >
        Entre com suas credenciais para acessar
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleLogin} noValidate>
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: colors.text.primary,
              mb: 1,
            }}
          >
            Email
          </Typography>
          <TextField
            fullWidth
            placeholder="seu@email.com"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: colors.text.light, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.white,
              },
            }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: colors.text.primary,
              mb: 1,
            }}
          >
            Senha
          </Typography>
          <TextField
            fullWidth
            placeholder="••••••••"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: colors.text.light, fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: colors.text.light }}
                  >
                    {showPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.white,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                size="small"
                sx={{
                  color: colors.text.light,
                  '&.Mui-checked': {
                    color: colors.primary,
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: '0.875rem', color: colors.text.secondary }}>
                Lembrar-me
              </Typography>
            }
          />
          <Link
            href="#"
            sx={{
              fontSize: '0.875rem',
              color: colors.primary,
              fontWeight: 500,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Esqueceu a senha?
          </Link>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            py: 1.5,
            backgroundColor: colors.accent,
            color: colors.dark,
            fontWeight: 600,
            fontSize: '1rem',
            '&:hover': {
              backgroundColor: '#c9e835',
            },
            '&:disabled': {
              backgroundColor: colors.border,
              color: colors.text.light,
            },
          }}
        >
          {loading ? 'Entrando...' : 'Entrar'}
        </Button>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography sx={{ color: colors.text.secondary, fontSize: '0.9375rem' }}>
            Solicite suas credenciais ao administrador do sistema.
          </Typography>
        </Box>
      </Box>
    </>
  );

  const renderContent = () => {
    switch (state) {
      case '2fa':
        return render2FAForm();
      case 'email_not_confirmed':
        return renderEmailNotConfirmed();
      default:
        return renderLoginForm();
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
      }}
    >
      {/* Left Side - Branding */}
      <Box
        sx={{
          width: '45%',
          backgroundColor: colors.dark,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          px: 8,
          py: 6,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.05,
            background: `
              radial-gradient(circle at 20% 80%, ${colors.accent} 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, ${colors.primary} 0%, transparent 50%)
            `,
          }}
        />

        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6, zIndex: 1 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '14px',
              backgroundColor: colors.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={LogoImage}
              alt="Expertise IA"
              sx={{
                width: 36,
                height: 36,
                objectFit: 'contain',
              }}
            />
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1.5rem',
              color: colors.white,
              fontFamily: 'Outfit, sans-serif',
            }}
          >
            Expertise IA
          </Typography>
        </Box>

        {/* Description */}
        <Typography
          sx={{
            color: colors.text.onDarkSecondary,
            fontSize: '1rem',
            mb: 5,
            maxWidth: 400,
            zIndex: 1,
          }}
        >
          Plataforma completa de ferramentas de inteligência artificial
        </Typography>

        {/* Features */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, zIndex: 1 }}>
          {features.map((feature, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  backgroundColor: 'rgba(219, 250, 59, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {feature.icon}
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: colors.white,
                    fontSize: '0.9375rem',
                    mb: 0.5,
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  sx={{
                    color: colors.text.onDarkSecondary,
                    fontSize: '0.8125rem',
                    lineHeight: 1.5,
                  }}
                >
                  {feature.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Right Side - Content */}
      <Box
        sx={{
          flex: 1,
          backgroundColor: colors.light,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 6 },
          py: 4,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
